import { memo, useCallback, useEffect } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import OnboardingTooltip from "components/OnboardingTooltip";
import {
  OnboardingTooltipBadgeTitle,
  OnboardingTooltipCloseButton,
  OnboardingTooltipContent,
  OnboardingTooltipDescription,
  OnboardingTooltipHeader,
  OnboardingTooltipLink,
} from "components/OnboardingTooltip/components";
import Bold from "components/primitives/Bold";
import Div from "components/primitives/Div";
import { linkVelocityBasedOneRepMax } from "config/knowledgebase-links";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useVelocityOnBoarding from "hooks/useVelocityOnBoarding";
import logEvent from "utils/logEvent";

function OneRepMaxVelocityOnboardingTooltipTitle() {
  const { t } = useTranslation();

  const handleLearnMoreClick = useCallback(
    () => logEvent("ATHLETE_PROFILE_ONE_REP_MAX_VELOCITY_ONBOARDING_LEARN_MORE_CLICK"),
    []
  );

  return (
    <OnboardingTooltipContent>
      <OnboardingTooltipHeader>
        <OnboardingTooltipBadgeTitle>
          {t("athlete_profile.one_rep_max_velocity_onboarding_title")}
        </OnboardingTooltipBadgeTitle>
        <OnboardingTooltipCloseButton />
      </OnboardingTooltipHeader>
      <OnboardingTooltipDescription>
        <Trans i18nKey="athlete_profile.one_rep_max_velocity_onboarding_description" components={{ Bold: <Bold /> }}>
          Track <Bold>strength progress over time with estimated 1RM from your athlete’s velocity data</Bold> - without
          the need to max out. You can also add and track known 1RM results manually.
        </Trans>
      </OnboardingTooltipDescription>
      <OnboardingTooltipLink href={linkVelocityBasedOneRepMax} onClick={handleLearnMoreClick} />
    </OnboardingTooltipContent>
  );
}

interface OneRepMaxVelocityOnboardingTooltipProps {
  children: ReactNode;
  /** @description Keeps the bubble closed while velocity-based 1RM is unavailable for the selected exercise. */
  disabled?: boolean;
}

/** Introduces velocity-based 1RM, pointing at the 1RM source toggle. */
function OneRepMaxVelocityOnboardingTooltip(props: OneRepMaxVelocityOnboardingTooltipProps) {
  const { t } = useTranslation();
  const { open, dismiss } = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteOneRepMaxVelocityTooltip);
  const isOpen = open && !props.disabled;

  useEffect(() => {
    if (!isOpen) return;

    logEvent("ATHLETE_PROFILE_ONE_REP_MAX_VELOCITY_ONBOARDING_VIEW");
  }, [isOpen]);

  const handleClose = useCallback(
    (event: SyntheticEvent | Event) => {
      logEvent("ATHLETE_PROFILE_ONE_REP_MAX_VELOCITY_ONBOARDING_DISMISS", {
        origin: event.type === "keydown" ? "escapeKey" : "closeButton",
      });
      dismiss();
    },
    [dismiss]
  );

  return (
    <OnboardingTooltip
      title={<OneRepMaxVelocityOnboardingTooltipTitle />}
      placement="top"
      open={isOpen}
      feature={t("athlete_profile.one_rep_max_velocity_onboarding_title")}
      onClose={handleClose}
      sx={{ width: "365px" }}
    >
      <Div>{props.children}</Div>
    </OnboardingTooltip>
  );
}

export default memo(OneRepMaxVelocityOnboardingTooltip);
